import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../api/client.ts";
import type { PageItemRow, SpaceOut } from "../api/types.gen.ts";
import { type ItemPick, outsideOf, spaceApi } from "./paths.ts";
import { tick } from "./resource.ts";

/** At most this many ids per request. */
const BATCH = 100;
/** Between one waiting link's icons arriving and the next's (page order), at most… */
const STAGGER_MS = 200;
/** …and all of them within this, the step shrinking when there are many. */
const STAGGER_ALL_MS = 3000;

/** Before the links waiting for `ids` show them: each its `--stagger` (a delay its CSS reads), in page order. */
function stagger(ids: readonly string[]): void {
  const waiting = [...document.querySelectorAll<HTMLElement>("[data-chip][data-pending]")].filter((el) =>
    ids.includes(el.dataset["chip"] ?? ""),
  );
  const step = Math.min(STAGGER_MS, STAGGER_ALL_MS / Math.max(1, waiting.length));
  waiting.forEach((el, i) => {
    el.style.setProperty("--stagger", `${Math.round(i * step)}ms`);
  });
}

/** Items named in texts (their links), by `space:id`: what their chips show. Null: not one the reader sees. */
const picks = signal<ReadonlyMap<string, ItemPick | null>>(new Map());
/** The live update (`tick`) each pick was loaded at: older ones are asked again (a title changed, an item shared). */
const loadedAt = new Map<string, number>();
/** Ids asked for and not loaded yet, by space (sent together, once this render's texts have all asked). */
const wanted = new Map<string, { space: SpaceOut; ids: Set<string> }>();
let pending = false;

function flush(): void {
  pending = false;
  const all = [...wanted.values()];
  wanted.clear();
  const at = tick.value;
  for (const { space, ids } of all) {
    const list = [...ids];
    for (let i = 0; i < list.length; i += BATCH) {
      const chunk = list.slice(i, i + BATCH);
      for (const id of chunk) {
        loadedAt.set(`${space.id}:${id}`, at);
      }
      api.get<PageItemRow>(spaceApi(space, "/items"), { ids: chunk.join(","), limit: chunk.length }).then(
        (page) => {
          stagger(chunk);
          const next = new Map(picks.value);
          for (const id of chunk) {
            next.set(`${space.id}:${id}`, page.data.find((r) => r.id === id) ?? null);
          }
          picks.value = next;
        },
        () => {
          // Asked again on the next live update, or by the next text naming them (loading until then).
          for (const id of chunk) {
            loadedAt.delete(`${space.id}:${id}`);
          }
        },
      );
    }
  }
}

/**
 * What the items `ids` are (kind, file, entity), for showing links to them: undefined while loading, null for one
 * the reader can't see. Asked in batches; outside the organization, from the items its page shows.
 */
export function useItemPicks(space: SpaceOut, ids: readonly string[]): (id: string) => ItemPick | null | undefined {
  const outside = outsideOf(space);
  const key = ids.join(",");
  // Live updates: what's shown is asked again (kept on screen until the answer comes).
  const live = tick.value;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `live` is a refetch trigger.
  useEffect(() => {
    if (outside) {
      return;
    }
    const missing = key.split(",").filter((id) => id !== "" && loadedAt.get(`${space.id}:${id}`) !== live);
    if (missing.length === 0) {
      return;
    }
    const entry = wanted.get(space.id) ?? { space, ids: new Set<string>() };
    for (const id of missing) {
      entry.ids.add(id);
    }
    wanted.set(space.id, entry);
    if (!pending) {
      pending = true;
      setTimeout(flush, 0);
    }
  }, [space, key, outside, live]);
  if (outside) {
    return (id) => outside.items.find((i) => i.id === id) ?? null;
  }
  const known = picks.value;
  return (id) => known.get(`${space.id}:${id}`);
}
